import { useEffect, useMemo, useState } from "react";
import { Box, Grid2, List, ListItem, ListItemIcon, ListItemText, Typography, Collapse, Checkbox, IconButton, Rating, Button, } from '@mui/material';
import { useGetUserPurchaseDetail } from "../../assets/api/UseStudentGetApi";
import useTabs from "../../shared/hook/useTabs";
import CustomDivider from "../../shared/utils/CustomDivider";
import TabsComponent from "../../shared/components/Tabs/TabComponent";
import TabPanel from "../../shared/components/Tabs/TabPanel";
import { SlotCard, TimeSlotChip } from "./StudentOnlineCourseDetail";
import { formatToAmPm } from "../../shared/formatter";
import { useParams } from "react-router-dom";
import { ImagePath, ImageRelativePath } from "../../shared/constants";
import { useDialog } from "../../shared/hook/useDialog";
import MainDialogBox from "../../shared/components/Dialog/MainDialogBox";
import { Formik, Field, Form } from "formik";
import { TextFormField } from "../../shared/components/formfields/TextFormField";
import RatingFormField from "../../shared/components/formfields/RatingFormField";
import * as Yup from "yup";
import { usePostCourseReview } from "../../assets/api/useStudentPostHooks";
import { useGetReviews } from "../../assets/nodeApi/getApi/getSchoolApi";
import ReviewRatingCom from "./ReviewRatingComGet";
import { TextSkeleton } from "../../shared/utils/SkeletonCom";
import StarIcon from '@mui/icons-material/Star';


const StudentCourseDetail = () => {
    const { course_id } = useParams();
    const { data, isLoading } = useGetUserPurchaseDetail({ course_id: course_id });

    const { value, handleChange } = useTabs(0);

    const [accordionChecks, setAccordionChecks] = useState([]);
    const [sprintTabs, setSprintTabs] = useState({});

    const { open: openReview, handleClickOpen: handleReviewOpen, handleClose: handleReviewClose } = useDialog();
    const mutation = usePostCourseReview(() => handleReviewClose());

    const { data: reviewsData, isLoading: isReviewsLoading } = useGetReviews({
        id: course_id, // or slug if API uses slug
        page: 1,
        limit: 3,
    });


    const validationSchema = Yup.object().shape({
        rating: Yup.number()
            .min(1, "Please select at least 1 star")
            .max(5, "Rating cannot exceed 5 stars")
            .required("Rating is required"),
        message: Yup.string()
            .trim()
            .min(10, "Feedback must be at least 10 characters")
            .max(300, "Feedback cannot exceed 300 characters")
            .required("Feedback is required"),
    });

    const handleSubmit = (values) => {
        const payload = {
            courseId: data?.data?.[0]?.event_id || course_id,
            trainerId: data?.data?.[0]?.trainer_id || 1,
            text: values.message,
            level: values.rating
        };
        mutation.mutate(payload);
    };

    // -------------------------
    // Prepare milestone+sprints
    // -------------------------
    useEffect(() => {
        const milestones = data?.data?.[0]?.order_details?.milestones || [];

        const formatted = milestones.map((milestone) => ({
            ...milestone,
            sprints: (milestone?.sprints || []).map((s) => ({
                sprints: s,
                checked: true,
            })),
        }));

        setAccordionChecks(formatted);
    }, [data]);

    // -------------------------
    // Sprint Tab Change
    // -------------------------
    const handleSprintChange = (milestoneIndex) => (_, newValue) => {
        setSprintTabs((prev) => ({
            ...prev,
            [milestoneIndex]: newValue,
        }));
    };

    // -------------------------
    // Build Sprint Tabs
    // -------------------------
    const allSprintTabs = useMemo(() => {
        return accordionChecks.map((milestone, milestoneIndex) => {
            return milestone.sprints.map((sprintObj, sprintIndex) => ({
                label: `Sprint ${sprintIndex + 1}`,
                component: (
                    <Box>
                        <Typography variant="h5">
                            {sprintObj?.sprints?.name} - Sprint {sprintIndex + 1}
                        </Typography>

                        <CustomDivider size="1rem" />

                        {sprintObj?.sprints?.description && (
                            <>
                                <Typography fontWeight={600}>Description</Typography>
                                <Typography variant="body2">
                                    {sprintObj?.sprints?.description}
                                </Typography>
                            </>
                        )}

                        <CustomDivider size="1rem" />

                        {sprintObj?.sprints?.learningoutcome && (
                            <>
                                <Typography fontWeight={600}>Learning Outcome</Typography>
                                <Typography variant="body2">
                                    {sprintObj?.sprints?.learningoutcome}
                                </Typography>
                            </>
                        )}

                        <CustomDivider size="1rem" />

                        {/* Sessions */}
                        {sprintObj?.sprints?.sessions?.map((session, idx) => (
                            <Box key={idx} display="flex" gap={1} mb={1}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        bgcolor: "primary.main",
                                        borderRadius: "50%",
                                        mt: "6px",
                                    }}
                                />
                                <Typography variant="body2">
                                    {session?.sessionname}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ),
            }));
        });
    }, [accordionChecks]);

    // -------------------------
    // Milestone Tabs
    // -------------------------
    const milestoneTabs = useMemo(() => {
        if (!data || accordionChecks.length === 0) return [];

        return accordionChecks.map((milestone, index) => {
            const sprintTabList = allSprintTabs[index] || [];
            const selectedSprint = sprintTabs[index] || 0;

            return {
                label: `Milestone ${index + 1}`,
                component: (
                    <>
                        {/* Top Section */}
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12} md={7}>
                                <Typography variant="h5">
                                    {milestone?.milestone_name}
                                </Typography>

                                <Typography variant="body2" mt={1}>
                                    From: {milestone?.start_date} &nbsp;&nbsp; To:{" "}
                                    {milestone?.end_date}
                                </Typography>

                                <CustomDivider size="1rem" />

                                {milestone?.description && (
                                    <>
                                        <Typography fontWeight={600}>Description</Typography>
                                        <Typography variant="body2">
                                            {milestone?.description}
                                        </Typography>
                                    </>
                                )}

                                <CustomDivider size="1rem" />

                                {milestone?.learningOutcome && (
                                    <>
                                        <Typography fontWeight={600}>
                                            Learning Outcome
                                        </Typography>
                                        <Typography variant="body2">
                                            {milestone?.learningOutcome}
                                        </Typography>
                                    </>
                                )}
                            </Grid2>
                        </Grid2>

                        <CustomDivider size="1.5rem" />

                        {/* Sprint Tabs */}
                        <TabsComponent
                            tabs={sprintTabList}
                            value={selectedSprint}
                            onChange={handleSprintChange(index)}
                        />

                        {sprintTabList.map((_, idx) => (
                            <TabPanel key={idx} value={selectedSprint} index={idx}>
                                {sprintTabList[selectedSprint]?.component}
                            </TabPanel>
                        ))}
                    </>
                ),
            };
        });
    }, [accordionChecks, allSprintTabs, sprintTabs, data]);

    // -------------------------
    // Render
    // -------------------------
    if (isLoading) return <Typography>Loading...</Typography>;

    const course = data?.data?.[0]?.order_details;

    return (
        <Box>

            <Grid2 size={{ xs: 12, md: 5 }}>
                <Box
                    src={`${ImagePath}${ImageRelativePath}${course?.cover_image}`}
                    component={"img"}
                    sx={{
                        width: "100%",
                        height: 350,
                        objectFit: "cover",
                        borderRadius: "0.5rem"

                    }}
                />
            </Grid2>
            <CustomDivider size="1rem" />
            {/* Course Info */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4">
                    {course?.course_name}
                </Typography>
                {/* <Button variant="contained" color="secondary" onClick={handleReviewOpen}>
                    Add Review
                </Button> */}
            </Box>

            <Typography variant="body2">
                Apartment: {course?.apartment_name}
            </Typography>

            <CustomDivider size="1rem" />

            {course?.description && (
                <>
                    <Typography fontWeight={600}>Description</Typography>
                    <Typography variant="body2">
                        {course?.description}
                    </Typography>
                </>
            )}

            <CustomDivider size="1rem" />
            {course?.short_description && (
                <>
                    <Typography variant="body2">
                        {course?.short_description}
                    </Typography>
                </>
            )}

            <CustomDivider size="1rem" />
            {course?.learningoutcome && (
                <>
                    <Typography fontWeight={600}>Learning Outcome</Typography>
                    <Typography variant="body2">
                        {course?.learningoutcome}
                    </Typography>
                </>
            )}

            <CustomDivider size="1rem" />
            <Typography fontWeight={600}>Sessions:</Typography>


            {/* <AppText variant='bodyMedium' color={theme.custom.darkGreyText}>
                    {offlineCourseDetail?.data?.[0]?.order_details?.description}
                </AppText> */}
            {/* {course?.sessions?.map((item, index) => (
                    <SloteCard key={index} isRemove={false} day={item?.day} start_time={item?.start_time} end_time={item?.end_time} />
                ))} */}

            {course?.sessions?.map((item, index) => (
                <SlotCard day={item?.day} timing={`${formatToAmPm(item?.start_time)}-${formatToAmPm(item?.end_time)}`} />
            ))}

            <CustomDivider size="2rem" />

            {/* Milestone Tabs */}
            {milestoneTabs.length > 0 && (
                <>
                    <TabsComponent
                        tabs={milestoneTabs}
                        value={value}
                        onChange={handleChange}
                    />

                    {milestoneTabs.map((_, index) => (
                        <TabPanel key={index} value={value} index={index}>
                            {milestoneTabs[value]?.component}
                        </TabPanel>
                    ))}
                </>
            )}

                <Box px={8} my={5}>
                    <CustomDivider orientation='horizontal' />
                    <Typography variant="h5" mb={3}>Review & Rating</Typography>

                    {isReviewsLoading ? (
                        <TextSkeleton line={3} />
                    ) : (
                        <>
                            {reviewsData?.overallReview && (
                                <Box mb={4} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="h2" color="red.main">
                                        {reviewsData.overallReview.averageRating}
                                    </Typography>
                                    <Box>
                                        <Rating
                                            value={Number(reviewsData.overallReview.averageRating)}
                                            readOnly
                                            precision={0.5}
                                            icon={<StarIcon sx={{ color: "red.main", fontSize: "25px" }} />}
                                            emptyIcon={<StarIcon sx={{ fontSize: "25px" }} />}
                                        />
                                        <Typography variant="body2" color="grey.400">
                                            Based on {reviewsData.overallReview.totalReviews} reviews
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {reviewsData?.reviews?.length > 0 ? (
                                reviewsData.reviews.map((review, index) => (
                                    <ReviewRatingCom key={review.id || index} review={review} />
                                ))
                            ) : (
                                <Typography variant="body1" color="grey.400">No reviews yet for this course.</Typography>
                            )}
                        </>
                    )}
                </Box>
        </Box>
    );
};

export default StudentCourseDetail;


